/**
 * Universal Logger Core Implementation
 * 
 * Main logger class providing structured, environment-aware logging
 * with performance tracking, correlation IDs, and queue-based processing.
 */

import { 
  UniversalLogLevel, 
  UniversalLogEntry, 
  LoggerConfig, 
  LoggerStats,
  EnvironmentConfig,
  LogNamespace,
  ErrorInfo,
  IUniversalLogger,
  MemoryUsage,
  MemoryDelta,
  MemoryThresholdCheck,
  OperationTiming,
  PerformanceMetrics
} from './types';

import {
  getEnvironmentConfig,
  createLoggerConfig,
  isLogLevelEnabled,
  generateCorrelationId,
  getMemoryUsage,
  calculateMemoryDelta,
  checkMemoryThresholds,
  sanitizeSensitiveData
} from './config';

import {
  defaultCorrelationIdProvider,
  getCorrelationId,
  getCorrelationIdContext
} from './correlation-id';

import { FilterEngine } from './filter-engine';

import { 
  FilterEngineConfig, 
  FilterResult,
  FilterAction,
  FilterPriority,
  FilterSeverity,
  SensitiveDataCategory 
} from './filter-types';

import { 
  createFilterConfigurationManager,
  FilterConfigurationManager 
} from './filter-config';

/**
 * Enhanced filtering integration options
 */
interface FilteringOptions {
  enabled: boolean;
  mode: 'basic' | 'enhanced';
  async: boolean;
  fallbackOnError: boolean;
  performanceMonitoring: boolean;
  auditLogging: boolean;
}

/**
 * Main Universal Logger class
 */
export class UniversalLogger implements IUniversalLogger {
  private static instance: UniversalLogger | null = null;
  private config: LoggerConfig;
  private envConfig: EnvironmentConfig;
  private logQueue: UniversalLogEntry[] = [];
  private isProcessing = false;
  private stats = {
    totalLogsSent: 0,
    errorCount: 0,
    warningCount: 0
  };
  private operationTimings = new Map<string, OperationTiming>();
  private currentRequestHeaders: Record<string, string> | null = null;
  
  // Enhanced filtering system
  private filterEngine: FilterEngine | null = null;
  private filterConfigManager: FilterConfigurationManager | null = null;
  private filteringOptions: FilteringOptions;
  private filterStats = {
    totalFiltered: 0,
    successfulFilters: 0,
    filterErrors: 0,
    fallbacksUsed: 0,
    averageFilterTime: 0
  };
  
  private constructor() {
    this.envConfig = getEnvironmentConfig();
    this.config = createLoggerConfig();
    
    // Initialize filtering options
    this.filteringOptions = {
      enabled: this.config.sensitiveDataFiltering,
      mode: this.config.environment === 'production' ? 'enhanced' : 'basic',
      async: true,
      fallbackOnError: true,
      performanceMonitoring: true,
      auditLogging: this.config.environment === 'production'
    };
    
    // Initialize enhanced filtering system if enabled
    if (this.filteringOptions.enabled && this.filteringOptions.mode === 'enhanced') {
      this.initializeEnhancedFiltering();
    }
  }
  
  /**
   * Initialize the enhanced filtering system
   */
  private initializeEnhancedFiltering(): void {
    try {
      // Create filter configuration manager
             this.filterConfigManager = createFilterConfigurationManager({
         metadata: {
           name: `Universal Logger Filter Config - ${this.config.environment}`,
           description: 'Sensitive data filtering configuration for Universal Logger',
           version: '1.0.0',
           author: 'Universal Logger System',
           createdAt: new Date().toISOString(),
           updatedAt: new Date().toISOString(),
           environment: this.config.environment as any,
           tags: ['universal-logger', 'filtering', this.config.environment]
         },
         globalSettings: {
           enabled: true,
           defaultAction: FilterAction.REDACT,
           defaultPriority: FilterPriority.MEDIUM,
           defaultSeverity: FilterSeverity.MEDIUM,
           cacheEnabled: true,
           cacheTtl: 60000,
           cacheSize: 1000,
           performanceMode: this.config.environment === 'production' ? 'fast' : 'balanced',
           failureMode: this.filteringOptions.fallbackOnError ? 'fallback' : 'strict',
           enableAuditLogging: this.filteringOptions.auditLogging,
           maxAuditEntries: 10000
         }
       });
      
      // Create and configure filter engine
      const filterConfig: FilterEngineConfig = {
        enabled: true,
        maxExecutionTime: 100, // 100ms max for logging
        maxRuleExecutionTime: 10, // 10ms max per rule
        enableCaching: true,
        cacheMaxSize: 1000,
        cacheTTL: 60000, // 1 minute
        enableParallelProcessing: false, // Keep simple for logging
        maxConcurrentRules: 1,
        failSafe: this.filteringOptions.fallbackOnError,
        logFilterErrors: this.config.environment !== 'production',
        rulesets: ['basic-pii', 'security-credentials', 'financial-data'],
        customRules: [],
        environmentOverrides: {},
        enableMetrics: this.filteringOptions.performanceMonitoring,
        enableAuditLog: this.filteringOptions.auditLogging,
        defaultAction: FilterAction.REDACT,
        defaultSeverity: FilterSeverity.MEDIUM
      };
      
      this.filterEngine = new FilterEngine(filterConfig);
      
      if (this.config.environment !== 'production') {
        this.info('Enhanced sensitive data filtering initialized', {
          filteringMode: this.filteringOptions.mode,
          async: this.filteringOptions.async,
          fallbackEnabled: this.filteringOptions.fallbackOnError
        }, undefined);
      }
    } catch (error) {
      // Fallback to basic filtering if enhanced filtering fails
      this.filteringOptions.mode = 'basic';
      this.filterEngine = null;
      this.filterConfigManager = null;
      
      if (this.config.environment !== 'production') {
        this.warn('Failed to initialize enhanced filtering, falling back to basic mode', {
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
  }
  
  /**
   * Enhanced sensitive data filtering using FilterEngine
   */
  private async enhancedSanitizeData(data: Record<string, any>): Promise<Record<string, any>> {
    if (!this.filterEngine || !this.filteringOptions.enabled) {
      return this.config.sensitiveDataFiltering ? sanitizeSensitiveData(data) : data;
    }
    
    const startTime = performance.now();
    
    try {
      const filterResult: FilterResult = this.filterEngine.filter(data, {
        fieldPath: 'log.details',
        fieldName: 'details',
        value: data,
        parentObject: { details: data },
        environment: this.config.environment,
        logLevel: 'info',
        namespace: 'universal-logger',
        startTime,
        timeout: 100
      });
      
      const endTime = performance.now();
      const filterDuration = endTime - startTime;
      
      // Update filter statistics
      this.updateFilterStats(true, filterDuration, filterResult.errors.length > 0);
      
      // Log filtering metrics if enabled
      if (this.filteringOptions.performanceMonitoring && filterResult.matches.length > 0) {
        this.debug('Sensitive data filtered', {
          matchesFound: filterResult.matches.length,
          rulesApplied: filterResult.rulesApplied,
          fieldsProcessed: filterResult.fieldsProcessed,
          filterDuration: `${filterDuration.toFixed(2)}ms`,
          categories: filterResult.matches.map(m => m.category)
        });
      }
      
      // Return filtered data or original if filtering failed
      return filterResult.success ? filterResult.filteredData : data;
      
    } catch (error) {
      const endTime = performance.now();
      const filterDuration = endTime - startTime;
      
      // Update error statistics
      this.updateFilterStats(false, filterDuration, true);
      
      // Use fallback filtering if enabled
      if (this.filteringOptions.fallbackOnError) {
        this.filterStats.fallbacksUsed++;
        return this.config.sensitiveDataFiltering ? sanitizeSensitiveData(data) : data;
      }
      
      // Log error if not in production
      if (this.config.environment !== 'production') {
        this.warn('Enhanced filtering failed', {
          error: error instanceof Error ? error.message : 'Unknown error',
          fallbackUsed: this.filteringOptions.fallbackOnError
        });
      }
      
      return data;
    }
  }
  
  /**
   * Synchronous version of enhanced filtering for performance-critical operations
   */
  private syncSanitizeData(data: Record<string, any>): Record<string, any> {
    if (!this.filterEngine || !this.filteringOptions.enabled) {
      return this.config.sensitiveDataFiltering ? sanitizeSensitiveData(data) : data;
    }
    
    try {
      const filterResult: FilterResult = this.filterEngine.filter(data);
      
      // Update statistics without async operations
      this.filterStats.totalFiltered++;
      if (filterResult.success) {
        this.filterStats.successfulFilters++;
      } else {
        this.filterStats.filterErrors++;
      }
      
      return filterResult.success ? filterResult.filteredData : data;
      
    } catch (error) {
      this.filterStats.filterErrors++;
      
      // Use fallback filtering
      if (this.filteringOptions.fallbackOnError) {
        this.filterStats.fallbacksUsed++;
        return this.config.sensitiveDataFiltering ? sanitizeSensitiveData(data) : data;
      }
      
      return data;
    }
  }
  
  /**
   * Update filter performance statistics
   */
  private updateFilterStats(success: boolean, duration: number, hasErrors: boolean): void {
    this.filterStats.totalFiltered++;
    
    if (success) {
      this.filterStats.successfulFilters++;
    } else {
      this.filterStats.filterErrors++;
    }
    
    // Update average filter time (rolling average)
    this.filterStats.averageFilterTime = 
      (this.filterStats.averageFilterTime * (this.filterStats.totalFiltered - 1) + duration) / 
      this.filterStats.totalFiltered;
  }
  
  /**
   * Get filtering statistics
   */
  getFilterStats(): typeof this.filterStats & { engine?: any } {
    const stats = { ...this.filterStats };
    
    if (this.filterEngine && this.filteringOptions.performanceMonitoring) {
      (stats as any).engine = this.filterEngine.getStats();
    }
    
    return stats;
  }
  
  /**
   * Configure filtering options at runtime
   */
  configureFiltering(options: Partial<FilteringOptions>): void {
    this.filteringOptions = { ...this.filteringOptions, ...options };
    
    // Reinitialize if mode changed
    if (options.mode && options.mode !== this.filteringOptions.mode) {
      if (options.mode === 'enhanced' && !this.filterEngine) {
        this.initializeEnhancedFiltering();
      } else if (options.mode === 'basic') {
        this.filterEngine = null;
        this.filterConfigManager = null;
      }
    }
  }
  
  /**
   * Get singleton instance
   */
  static getInstance(): UniversalLogger {
    if (!this.instance) {
      this.instance = new UniversalLogger();
    }
    return this.instance;
  }

  /**
   * Reset singleton instance (for testing)
   */
  static resetInstance(): void {
    this.instance = null;
  }
  
  /**
   * Create performance metrics object from provided data
   */
  private createPerformanceMetrics(
    duration?: number,
    startTime?: number,
    endTime?: number,
    memoryUsage?: MemoryUsage,
    memoryDelta?: MemoryDelta,
    operationName?: string,
    functionName?: string,
    status?: 'success' | 'error' | 'warning',
    isAsync?: boolean
  ): PerformanceMetrics | undefined {
    if (!this.config.performanceTracking) {
      return undefined;
    }
    
    const metrics: PerformanceMetrics = {};
    
    // Add timing information
    if (duration !== undefined) metrics.duration = duration;
    if (startTime !== undefined) metrics.startTime = startTime;
    if (endTime !== undefined) metrics.endTime = endTime;
    
    // Add memory information
    if (memoryUsage) {
      metrics.memoryUsage = memoryUsage.used;
      metrics.memoryUsageDetails = {
        used: memoryUsage.used,
        usedMB: Math.round((memoryUsage.used / (1024 * 1024)) * 100) / 100,
        percentage: memoryUsage.percentage,
        timestamp: memoryUsage.timestamp
      };
      
      // Check for memory warnings
      const thresholdCheck = checkMemoryThresholds(memoryUsage);
      if (thresholdCheck.isWarning) {
        metrics.memoryWarning = thresholdCheck;
      }
    }
    
    // Add memory delta if available
    if (memoryDelta) {
      metrics.memoryDelta = {
        usedDelta: memoryDelta.usedDelta,
        usedDeltaMB: memoryDelta.usedDeltaMB,
        timeDelta: memoryDelta.timeDelta
      };
      
      // Add warning for significant memory increases
      if (memoryDelta.usedDeltaMB > 10) {
        metrics.memoryIncrease = `Operation increased memory by ${memoryDelta.usedDeltaMB}MB`;
      }
    }
    
    // Add operation context
    if (operationName) metrics.operationName = operationName;
    if (functionName) metrics.functionName = functionName;
    if (status) metrics.status = status;
    if (isAsync) metrics.async = isAsync;
    
    // Only return the metrics object if it has any properties
    return Object.keys(metrics).length > 0 ? metrics : undefined;
  }

  /**
   * Create a structured log entry
   */
  private createLogEntry(
    level: UniversalLogLevel,
    namespace: LogNamespace,
    message: string,
    correlationId: string,
    details: Record<string, any> = {},
    error?: Error,
    duration?: number,
    performanceMetrics?: PerformanceMetrics
  ): UniversalLogEntry {
    const entry: UniversalLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      namespace,
      correlationId,
      message,
      details: this.filteringOptions.enabled ? this.syncSanitizeData(details) : details,
      environment: this.config.environment
    };
    
    // Add performance metrics if enabled
    if (this.config.performanceTracking) {
      // Backward compatibility fields
      entry.duration = duration;
      const currentMemory = getMemoryUsage();
      entry.memoryUsage = currentMemory.used;
      
      // Enhanced performance metrics
      if (performanceMetrics) {
        entry.performance = performanceMetrics;
      } else if (duration !== undefined) {
        // Create basic performance metrics if duration is provided
        entry.performance = this.createPerformanceMetrics(
          duration,
          undefined,
          undefined,
          currentMemory
        );
      }
    }
    
    // Add error information if provided
    if (error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        code: (error as any).code
      };
    }
    
    return entry;
  }
  
  /**
   * Check if logging is enabled for a given level
   */
  private isLoggingEnabled(level: UniversalLogLevel): boolean {
    return isLogLevelEnabled(level, this.envConfig);
  }
  
  /**
   * Output log entry to console if enabled
   */
  private outputToConsole(entry: UniversalLogEntry): void {
    if (!this.config.consoleEnabled) {
      return;
    }
    
    const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}] [${entry.namespace}] [${entry.correlationId}]`;
    const message = `${prefix} ${entry.message}`;
    
    // Use appropriate console method based on log level
    switch (entry.level) {
      case UniversalLogLevel.ERROR:
        console.error(message, entry.details, entry.error);
        break;
      case UniversalLogLevel.WARN:
        console.warn(message, entry.details);
        break;
      case UniversalLogLevel.INFO:
        console.log(message, entry.details);
        break;
      case UniversalLogLevel.DEBUG:
        console.log(message, entry.details);
        break;
    }
  }
  
  /**
   * Determine if a log level should be sent to Sentry based on configuration
   */
  private shouldSendToSentry(level: UniversalLogLevel): boolean {
    // Always send errors
    if (level === UniversalLogLevel.ERROR) {
      return true;
    }
    
    // Send warnings if explicitly configured
    if (level === UniversalLogLevel.WARN && this.config.sentryIncludeWarnings) {
      return true;
    }
    
    // Check against minimum Sentry level
    const levels = [UniversalLogLevel.ERROR, UniversalLogLevel.WARN, UniversalLogLevel.INFO, UniversalLogLevel.DEBUG];
    const sentryLevelIndex = levels.indexOf(this.config.sentryLevel);
    const currentLevelIndex = levels.indexOf(level);
    
    return currentLevelIndex <= sentryLevelIndex;
  }

  /**
   * Map Universal Logger levels to Sentry severity levels
   */
  private mapToSentryLevel(level: UniversalLogLevel): 'error' | 'warning' | 'info' | 'debug' {
    switch (level) {
      case UniversalLogLevel.ERROR:
        return 'error';
      case UniversalLogLevel.WARN:
        return 'warning';
      case UniversalLogLevel.INFO:
        return 'info';
      case UniversalLogLevel.DEBUG:
        return 'debug';
      default:
        return 'error';
    }
  }

  /**
   * Enhanced Sentry integration for production-grade error reporting
   * Routes error-level logs to Sentry with comprehensive context and metadata
   */
  private async sendToSentry(entry: UniversalLogEntry): Promise<void> {
    // Check if Sentry is enabled and if this log level should be sent
    if (!this.config.sentryEnabled) {
      return;
    }

    // Determine if this log level should be sent to Sentry
    const shouldSend = this.shouldSendToSentry(entry.level);
    if (!shouldSend) {
      return;
    }

    try {
      // Dynamic import to avoid bundling Sentry when not used
      const { captureException, captureMessage, withScope, setTag, setExtra, setContext } = await import('@sentry/browser');

      // Use Sentry scope to enrich error context
      withScope((scope) => {
        // Set fundamental identification tags
        scope.setTag('namespace', entry.namespace);
        scope.setTag('correlationId', entry.correlationId);
        scope.setTag('environment', entry.environment || this.config.environment);
        scope.setTag('logLevel', entry.level);
        
        // Add timestamp and performance context
        scope.setTag('logTimestamp', entry.timestamp);
        if (entry.duration) {
          scope.setTag('operationDuration', `${Math.round(entry.duration)}ms`);
        }

        // Set user context if available
        if (entry.userId) {
          scope.setUser({
            id: entry.userId,
            ...(entry.sessionId && { sessionId: entry.sessionId })
          });
        }

        // Add performance metrics as context
        if (entry.performance) {
          scope.setContext('performance', {
            duration: entry.performance.duration,
            memoryUsage: entry.performance.memoryUsageDetails,
            memoryDelta: entry.performance.memoryDelta,
            operationName: entry.performance.operationName,
            status: entry.performance.status,
            isAsync: entry.performance.async
          });
        }

        // Add correlation tracking context
        scope.setContext('correlationTracking', {
          correlationId: entry.correlationId,
          requestHeaders: this.currentRequestHeaders ? Object.keys(this.currentRequestHeaders) : undefined,
          queueLength: this.logQueue.length,
          logEntryNumber: this.stats.totalLogsSent + 1
        });

        // Add enhanced details as extra context (sanitized)
        scope.setExtra('logEntry', {
          timestamp: entry.timestamp,
          namespace: entry.namespace,
          message: entry.message,
          details: entry.details,
          correlationId: entry.correlationId
        });

        // Add Universal Logger system context
        scope.setContext('universalLogger', {
          version: '1.0.0',
          queueEnabled: this.config.queueEnabled,
          environment: this.config.environment,
          filteringEnabled: this.filteringOptions.enabled,
          filteringMode: this.filteringOptions.mode,
          totalLogsSent: this.stats.totalLogsSent,
          errorCount: this.stats.errorCount
        });

                 // Capture the error or message with enhanced context
         if (entry.error) {
           // Create a proper Error object for Sentry with full stack trace
           const errorToCapture = new Error(entry.error.message);
           errorToCapture.name = entry.error.name || 'UniversalLoggerError';
           errorToCapture.stack = entry.error.stack;
           
           // Add custom properties to error object
           (errorToCapture as any).correlationId = entry.correlationId;
           (errorToCapture as any).namespace = entry.namespace;
           (errorToCapture as any).loggerContext = entry.details;
           
           captureException(errorToCapture);
         } else {
           // Map Universal Logger levels to Sentry severity levels
           const sentryLevel = this.mapToSentryLevel(entry.level);
           captureMessage(entry.message, sentryLevel);
         }
      });

      // Update internal statistics
      this.stats.totalLogsSent++;

    } catch (sentryError) {
      // Fail gracefully - Sentry errors should never break the application
      if (this.envConfig.isDevelopment) {
        console.error('Universal Logger: Failed to send error to Sentry:', sentryError);
        console.error('Original log entry:', {
          level: entry.level,
          message: entry.message,
          namespace: entry.namespace,
          correlationId: entry.correlationId
        });
      }
      
      // Track Sentry failures for monitoring
      this.stats.errorCount++;
    }
  }
  
  /**
   * Process the log queue asynchronously
   */
  private async processLogQueue(): Promise<void> {
    if (this.isProcessing || this.logQueue.length === 0) {
      return;
    }
    
    this.isProcessing = true;
    
    try {
      const entriesToProcess = [...this.logQueue];
      this.logQueue = [];
      
      for (const entry of entriesToProcess) {
        // Output to console
        this.outputToConsole(entry);
        
        // Send to Sentry if applicable
        await this.sendToSentry(entry);
        
        // Update statistics
        this.stats.totalLogsSent++;
        if (entry.level === UniversalLogLevel.ERROR) {
          this.stats.errorCount++;
        } else if (entry.level === UniversalLogLevel.WARN) {
          this.stats.warningCount++;
        }
      }
    } catch (error) {
      // Fail silently - don't let logging errors break the application
      if (this.envConfig.isDevelopment) {
        console.error('Error processing log queue:', error);
      }
    } finally {
      this.isProcessing = false;
    }
  }
  
  /**
   * Add log entry to queue
   */
  private queueLog(entry: UniversalLogEntry): void {
    // Respect max log entries limit
    if (this.logQueue.length >= this.config.maxLogEntries) {
      this.logQueue.shift(); // Remove oldest entry
    }
    
    this.logQueue.push(entry);
    
    // Process queue asynchronously
    if (this.config.queueEnabled) {
      setTimeout(() => this.processLogQueue(), 0);
    } else {
      // Synchronous processing for testing or special cases
      this.processLogQueue();
    }
  }
  
  /**
   * Core logging method
   */
  private log(
    level: UniversalLogLevel,
    namespace: LogNamespace,
    message: string,
    details: Record<string, any> = {},
    correlationId?: string,
    error?: Error,
    duration?: number,
    performanceMetrics?: PerformanceMetrics
  ): void {
    if (!this.isLoggingEnabled(level)) {
      return;
    }
    
    // Use new correlation ID provider with request header support
    const logCorrelationId = correlationId || getCorrelationId(undefined, this.currentRequestHeaders || undefined);
    
    const entry = this.createLogEntry(
      level,
      namespace,
      message,
      logCorrelationId,
      details,
      error,
      duration,
      performanceMetrics
    );
    
    this.queueLog(entry);
  }
  
  // Public logging methods with default namespace
  error(message: string, details?: Record<string, any>, correlationId?: string, error?: Error): void {
    this.log(UniversalLogLevel.ERROR, LogNamespace.API, message, details, correlationId, error);
  }
  
  warn(message: string, details?: Record<string, any>, correlationId?: string): void {
    this.log(UniversalLogLevel.WARN, LogNamespace.API, message, details, correlationId);
  }
  
  info(message: string, details?: Record<string, any>, correlationId?: string): void {
    this.log(UniversalLogLevel.INFO, LogNamespace.API, message, details, correlationId);
  }
  
  debug(message: string, details?: Record<string, any>, correlationId?: string): void {
    this.log(UniversalLogLevel.DEBUG, LogNamespace.API, message, details, correlationId);
  }
  
  // Operation tracking methods
  startOperation(operationName: string, details?: Record<string, any>): string {
    const correlationId = getCorrelationId(undefined, this.currentRequestHeaders || undefined);
    const startTime = performance.now();
    const startMemory = this.config.performanceTracking ? getMemoryUsage() : undefined;
    
    // Store timing and memory information
    this.operationTimings.set(correlationId, {
      operationId: correlationId,
      startTime,
      startMemory
    });
    
    const logDetails: Record<string, any> = { 
      operationName, 
      startTime,
      ...details 
    };
    
    // Add memory info if tracking is enabled
    if (startMemory) {
      logDetails.startMemory = {
        used: startMemory.used,
        usedMB: Math.round((startMemory.used / (1024 * 1024)) * 100) / 100,
        percentage: startMemory.percentage
      };
      
      // Check for memory warnings
      const thresholdCheck = checkMemoryThresholds(startMemory);
      if (thresholdCheck.isWarning) {
        logDetails.memoryWarning = thresholdCheck;
      }
    }
    
    this.info(
      `Starting operation: ${operationName}`,
      logDetails,
      correlationId
    );
    
    return correlationId;
  }
  
  endOperation(correlationId: string, details?: Record<string, any>): void {
    const timing = this.operationTimings.get(correlationId);
    
    if (timing) {
      const endTime = performance.now();
      const duration = endTime - timing.startTime;
      const endMemory = this.config.performanceTracking ? getMemoryUsage() : undefined;
      
      // Calculate memory delta if we have both start and end memory
      let memoryDelta: MemoryDelta | undefined;
      if (timing.startMemory && endMemory) {
        memoryDelta = calculateMemoryDelta(timing.startMemory, endMemory);
      }
      
      // Update timing info
      timing.endTime = endTime;
      timing.duration = duration;
      timing.endMemory = endMemory;
      timing.memoryDelta = memoryDelta;
      
      const logDetails: Record<string, any> = { 
        duration: Math.round(duration * 100) / 100,
        startTime: timing.startTime,
        endTime: endTime,
        ...details 
      };
      
      // Add memory info if tracking is enabled
      if (endMemory) {
        logDetails.endMemory = {
          used: endMemory.used,
          usedMB: Math.round((endMemory.used / (1024 * 1024)) * 100) / 100,
          percentage: endMemory.percentage
        };
        
        // Check for memory warnings
        const thresholdCheck = checkMemoryThresholds(endMemory);
        if (thresholdCheck.isWarning) {
          logDetails.memoryWarning = thresholdCheck;
        }
      }
      
      // Add memory delta if available
      if (memoryDelta) {
        logDetails.memoryDelta = {
          usedDeltaMB: memoryDelta.usedDeltaMB,
          usedDelta: memoryDelta.usedDelta,
          timeDelta: Math.round(memoryDelta.timeDelta * 100) / 100
        };
        
        // Warn about significant memory increases
        if (memoryDelta.usedDeltaMB > 10) {
          logDetails.memoryIncrease = `Operation increased memory by ${memoryDelta.usedDeltaMB}MB`;
        }
      }
      
      // Create enhanced performance metrics
      const performanceMetrics = this.createPerformanceMetrics(
        duration,
        timing.startTime,
        endTime,
        endMemory,
        memoryDelta,
        'operation'
      );
      
      this.log(
        UniversalLogLevel.INFO,
        LogNamespace.PERFORMANCE,
        `Completed operation`,
        logDetails,
        correlationId,
        undefined,
        duration,
        performanceMetrics
      );
      
      // Clean up timing info to prevent memory leaks
      this.operationTimings.delete(correlationId);
    } else {
      this.warn(
        `Attempted to end operation with unknown correlation ID: ${correlationId}`,
        details,
        correlationId
      );
    }
  }

  /**
   * Measure the execution time of a synchronous function
   */
  timeFunction<T>(
    functionName: string,
    fn: () => T,
    namespace: LogNamespace = LogNamespace.PERFORMANCE,
    details?: Record<string, any>
  ): T {
    const startTime = performance.now();
    const startMemory = this.config.performanceTracking ? getMemoryUsage() : undefined;
    const correlationId = generateCorrelationId();
    
    try {
      const result = fn();
      const endTime = performance.now();
      const duration = endTime - startTime;
      const endMemory = this.config.performanceTracking ? getMemoryUsage() : undefined;
      
      const logDetails: Record<string, any> = {
        functionName,
        duration: Math.round(duration * 100) / 100,
        status: 'success',
        startTime,
        endTime,
        ...details
      };
      
      // Add memory tracking if enabled
      if (startMemory && endMemory) {
        const memoryDelta = calculateMemoryDelta(startMemory, endMemory);
        logDetails.memoryDelta = {
          usedDeltaMB: memoryDelta.usedDeltaMB,
          usedDelta: memoryDelta.usedDelta
        };
        
        // Check for memory warnings
        const thresholdCheck = checkMemoryThresholds(endMemory);
        if (thresholdCheck.isWarning) {
          logDetails.memoryWarning = thresholdCheck;
        }
      }
      
      // Create enhanced performance metrics
      const performanceMetrics = this.createPerformanceMetrics(
        duration,
        startTime,
        endTime,
        endMemory,
        startMemory && endMemory ? calculateMemoryDelta(startMemory, endMemory) : undefined,
        undefined,
        functionName,
        'success'
      );
      
      this.log(
        UniversalLogLevel.INFO,
        namespace,
        `Function '${functionName}' executed successfully`,
        logDetails,
        correlationId,
        undefined,
        duration,
        performanceMetrics
      );
      
      return result;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      const endMemory = this.config.performanceTracking ? getMemoryUsage() : undefined;
      
      const logDetails: Record<string, any> = {
        functionName,
        duration: Math.round(duration * 100) / 100,
        status: 'error',
        startTime,
        endTime,
        ...details
      };
      
      // Add memory tracking if enabled
      if (startMemory && endMemory) {
        const memoryDelta = calculateMemoryDelta(startMemory, endMemory);
        logDetails.memoryDelta = {
          usedDeltaMB: memoryDelta.usedDeltaMB,
          usedDelta: memoryDelta.usedDelta
        };
      }
      
      // Create enhanced performance metrics for error case
      const performanceMetrics = this.createPerformanceMetrics(
        duration,
        startTime,
        endTime,
        endMemory,
        startMemory && endMemory ? calculateMemoryDelta(startMemory, endMemory) : undefined,
        undefined,
        functionName,
        'error'
      );
      
      this.log(
        UniversalLogLevel.ERROR,
        namespace,
        `Function '${functionName}' threw an error`,
        logDetails,
        correlationId,
        error as Error,
        duration,
        performanceMetrics
      );
      
      throw error;
    }
  }

  /**
   * Measure the execution time of an asynchronous function
   */
  async timeAsyncFunction<T>(
    functionName: string,
    fn: () => Promise<T>,
    namespace: LogNamespace = LogNamespace.PERFORMANCE,
    details?: Record<string, any>
  ): Promise<T> {
    const startTime = performance.now();
    const startMemory = this.config.performanceTracking ? getMemoryUsage() : undefined;
    const correlationId = generateCorrelationId();
    
    try {
      const result = await fn();
      const endTime = performance.now();
      const duration = endTime - startTime;
      const endMemory = this.config.performanceTracking ? getMemoryUsage() : undefined;
      
      const logDetails: Record<string, any> = {
        functionName,
        duration: Math.round(duration * 100) / 100,
        status: 'success',
        startTime,
        endTime,
        async: true,
        ...details
      };
      
      // Add memory tracking if enabled
      if (startMemory && endMemory) {
        const memoryDelta = calculateMemoryDelta(startMemory, endMemory);
        logDetails.memoryDelta = {
          usedDeltaMB: memoryDelta.usedDeltaMB,
          usedDelta: memoryDelta.usedDelta
        };
        
        // Check for memory warnings
        const thresholdCheck = checkMemoryThresholds(endMemory);
        if (thresholdCheck.isWarning) {
          logDetails.memoryWarning = thresholdCheck;
        }
      }
      
      // Create enhanced performance metrics
      const performanceMetrics = this.createPerformanceMetrics(
        duration,
        startTime,
        endTime,
        endMemory,
        startMemory && endMemory ? calculateMemoryDelta(startMemory, endMemory) : undefined,
        undefined,
        functionName,
        'success',
        true
      );
      
      this.log(
        UniversalLogLevel.INFO,
        namespace,
        `Async function '${functionName}' executed successfully`,
        logDetails,
        correlationId,
        undefined,
        duration,
        performanceMetrics
      );
      
      return result;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      const endMemory = this.config.performanceTracking ? getMemoryUsage() : undefined;
      
      const logDetails: Record<string, any> = {
        functionName,
        duration: Math.round(duration * 100) / 100,
        status: 'error',
        startTime,
        endTime,
        async: true,
        ...details
      };
      
      // Add memory tracking if enabled
      if (startMemory && endMemory) {
        const memoryDelta = calculateMemoryDelta(startMemory, endMemory);
        logDetails.memoryDelta = {
          usedDeltaMB: memoryDelta.usedDeltaMB,
          usedDelta: memoryDelta.usedDelta
        };
      }
      
      // Create enhanced performance metrics for error case
      const performanceMetrics = this.createPerformanceMetrics(
        duration,
        startTime,
        endTime,
        endMemory,
        startMemory && endMemory ? calculateMemoryDelta(startMemory, endMemory) : undefined,
        undefined,
        functionName,
        'error',
        true
      );
      
      this.log(
        UniversalLogLevel.ERROR,
        namespace,
        `Async function '${functionName}' threw an error`,
        logDetails,
        correlationId,
        error as Error,
        duration,
        performanceMetrics
      );
      
      throw error;
    }
  }

  /**
   * Create a high-precision timestamp for performance measurement
   */
  getPerformanceTimestamp(): number {
    return performance.now();
  }

  /**
   * Calculate duration between two performance timestamps
   */
  calculateDuration(startTime: number, endTime?: number): number {
    const end = endTime ?? performance.now();
    return Math.round((end - startTime) * 100) / 100;
  }
  
  // Configuration methods
  getConfig(): LoggerConfig {
    return { ...this.config };
  }
  
  updateConfig(configUpdate: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...configUpdate };
    
    // Re-evaluate environment config if console settings changed
    if ('consoleEnabled' in configUpdate || 'consoleLevel' in configUpdate) {
      this.envConfig = getEnvironmentConfig();
    }
  }
  
  // Statistics and monitoring
  getStats(): LoggerStats {
    const memoryUsage = getMemoryUsage();
    
    return {
      queueLength: this.logQueue.length,
      isProcessing: this.isProcessing,
      logLevel: this.envConfig.logLevel,
      loggingEnabled: true,
      totalLogsSent: this.stats.totalLogsSent,
      errorCount: this.stats.errorCount,
      warningCount: this.stats.warningCount,
      environmentConfig: this.envConfig,
      memoryUsage,
      // Additional properties for compatibility
      totalLogEntries: this.stats.totalLogsSent,
      queueSize: this.logQueue.length
    };
  }
  
  // Queue management
  async flush(): Promise<void> {
    await this.processLogQueue();
  }
  
  clear(): void {
    this.logQueue = [];
    this.operationTimings.clear();
    this.stats = {
      totalLogsSent: 0,
      errorCount: 0,
      warningCount: 0
    };
  }
  
  /**
   * Set request headers for correlation ID extraction
   */
  setRequestHeaders(headers: Record<string, string> | null): void {
    this.currentRequestHeaders = headers;
  }

  /**
   * Get current request headers
   */
  getCurrentRequestHeaders(): Record<string, string> | null {
    return this.currentRequestHeaders;
  }

  /**
   * Get current correlation ID context
   */
  getCurrentCorrelationIdContext() {
    return defaultCorrelationIdProvider.getCurrentContext();
  }

  /**
   * Clear correlation ID context (typically at end of request)
   */
  clearCorrelationIdContext(): void {
    defaultCorrelationIdProvider.clearContext();
    this.currentRequestHeaders = null;
  }

  /**
   * Create child correlation ID for nested operations
   */
  createChildCorrelationId(operationName: string): string {
    return defaultCorrelationIdProvider.createChildCorrelationId(operationName);
  }

  /**
   * Create namespace-specific logger
   */
  createNamespaceLogger(namespace: LogNamespace): IUniversalLogger {
    return {
      error: (message, details, correlationId, error) => 
        this.log(UniversalLogLevel.ERROR, namespace, message, details, correlationId, error),
      warn: (message, details, correlationId) => 
        this.log(UniversalLogLevel.WARN, namespace, message, details, correlationId),
      info: (message, details, correlationId) => 
        this.log(UniversalLogLevel.INFO, namespace, message, details, correlationId),
      debug: (message, details, correlationId) => 
        this.log(UniversalLogLevel.DEBUG, namespace, message, details, correlationId),
      startOperation: this.startOperation.bind(this),
      endOperation: this.endOperation.bind(this),
      // Function timing methods
      timeFunction: <T>(functionName: string, fn: () => T, ns?: LogNamespace, details?: Record<string, any>) =>
        this.timeFunction(functionName, fn, ns || namespace, details),
      timeAsyncFunction: <T>(functionName: string, fn: () => Promise<T>, ns?: LogNamespace, details?: Record<string, any>) =>
        this.timeAsyncFunction(functionName, fn, ns || namespace, details),
      getPerformanceTimestamp: this.getPerformanceTimestamp.bind(this),
      calculateDuration: this.calculateDuration.bind(this),
      getConfig: this.getConfig.bind(this),
      updateConfig: this.updateConfig.bind(this),
      getStats: this.getStats.bind(this),
      flush: this.flush.bind(this),
      clear: this.clear.bind(this),
      // Correlation ID management methods
      setRequestHeaders: this.setRequestHeaders.bind(this),
      getCurrentRequestHeaders: this.getCurrentRequestHeaders.bind(this),
      getCurrentCorrelationIdContext: this.getCurrentCorrelationIdContext.bind(this),
      clearCorrelationIdContext: this.clearCorrelationIdContext.bind(this),
      createChildCorrelationId: this.createChildCorrelationId.bind(this)
    };
  }
}

/**
 * Convenience function to get logger instance
 */
export const getUniversalLogger = (): UniversalLogger => UniversalLogger.getInstance(); 